import React from 'react';
import { ColorDropBold, ShadowBold, TextBold } from '@wix/wix-base-ui/icons';

import {
  Composites,
  Divider,
  OpacityPicker,
  ColorPickerInput,
  VerticalTabsCustom,
  HorizontalTabs,
  Text,
  CustomScroll,
  Button,
  TextLabel,
  Slider,
  ToggleSwitch,
  DropDownOption,
  DropDown,
} from '@wix/wix-base-ui';

import {
  getVideoBackgroundObject,
  getImageBackgroundObject,
  isSmallerFromContainer,
  BackgroundData,
  alignTypes,
  MMGRInfo,
  mediaManagerDefaultPaths,
  payloadMediaTypes,
  VideoData,
  ImageData,
  fittingTypes,
  ColorOrGradient,
  getMediaType,
  MEDIA_TYPES,
  getImageDisplayData,
  getMediaURl,
  MMGRFileInfo,
  BlendMode,
  getColorOrGradient,
  COLOR_DEFAULTS,
  getNewColorLayers,
  getColorOpacityValue,
  getResolvedColor,
} from '@wix/background-media-utils';

import type { FittingType } from '@wix/image-kit';
import { ColorPickerPanelMode as PanelMode } from '@wix/wow-utils';
import type {
  SVGMeasureRef,
  TextMaskDesign,
  BiParams,
  ITextMaskDesignPanelPropsClassic,
} from '../../../TextMaskPanels.types';
import { ShadowSection } from '../../components/ShadowSection.editor';
import {
  COLOR_PICKER_X_OFFSET,
  COLOR_PICKER_Y_OFFSET,
  mediaCategory,
  BLEND_MODES_IMG_ONLY,
  BLEND_MODES_BASE,
  mediaDefaults,
} from './TextMask.editor.consts';

import styles from './textMaskPanel.editor.scss';
import trnKeys from './TextMask.classic.translations.json';
import { SvgMeasure } from '../../../common/SvgMeasure';
import { TextSection } from '../../components/TextSection.editor';
import { ColorOverlayData } from '../../components/TextMaskDesignPanel.types';

const DIMENSIONS = {
  PANEL_WIDTH: 240,
  PANEL_HEIGHT: 526,
  MEDIA_HEIGHT: 123,
};

const DEFAULT_OVERLAY: ColorOverlayData = {
  colorOverlay: '#000000',
  colorOverlayOpacity: 0.5,
  overlayBlending: {
    blendMode: 'normal',
    opacity: 1,
  },
};

function getImageData(data: BackgroundData): ImageData | undefined {
  if (getMediaType(data) === MEDIA_TYPES.IMAGE) {
    return data.mediaRef as ImageData;
  } else if (getMediaType(data) === MEDIA_TYPES.VIDEO) {
    return (data.mediaRef as VideoData).posterImageRef;
  }
  return undefined;
}

function getPreviewImage(
  data: BackgroundData,
  fittingType = fittingTypes.SCALE_TO_FIT,
  width = DIMENSIONS.PANEL_WIDTH,
  height = DIMENSIONS.MEDIA_HEIGHT,
) {
  const imageData = getImageData(data);
  if (!imageData) {
    return '';
  }
  const previewDisplayData = getImageDisplayData({
    fittingType,
    alignType: alignTypes.CENTER,
    imageUri: imageData.uri,
    srcImageWidth: imageData.width,
    srcImageHeight: imageData.height,
    containerWidth: width,
    containerHeight: height,
    pixelAspectRatio: 1,
  });
  return getMediaURl(previewDisplayData.uri!);
}

export const TextMaskDesignPanelClassic: React.FC<
  ITextMaskDesignPanelPropsClassic
> = ({
  fontThemes,
  allowThemeControl,
  fonts,
  compData,
  compDesign,
  updateData,
  updateDesign,
  compLayout,
  updateLayout,
  openColorPicker: openColorPickerFromProps,
  colorResolver,
  mediaServices,
  openUploadFonts,
  openLanguagesPanel,
  translate,
  sendBI,
  allowAutoScaleControl,
}) => {
  const {
    align,
    family,
    size,
    weight,
    style,
    decoration,
    transform,
    letterSpacing,
    lineHeight,
    viewBox,
    shadow,
    media,
  } = compDesign as TextMaskDesign;

  const {
    colorLayers,
    colorOverlay,
    colorOverlayOpacity,
    overlayBlending = DEFAULT_OVERLAY.overlayBlending,
    mediaRef,
    ...mediaNoColors
  } = media;

  const isVideo = mediaRef?.type === 'WixVideo';
  const previousMedia = React.useRef<BackgroundData>();
  const measureSvgRef = React.useRef<SVGMeasureRef>(null);
  const textLabelType = 'T02';
  const { headingLevel, isDecorative, direction } = compData;
  const blendModes = {
    ...BLEND_MODES_BASE,
    ...(isVideo ? {} : BLEND_MODES_IMG_ONLY),
  };
  const backgroundType =
    getMediaType(media) === MEDIA_TYPES.COLOR ? 'gradient' : 'media';

  const panelTabOptions = [
    {
      value: 'color',
      label: translate(trnKeys.section_fill),
      symbol: <ColorDropBold />,
    },
    {
      value: 'text',
      label: translate(trnKeys.section_text),
      symbol: <TextBold />,
    },
    {
      value: 'shadow',
      label: translate(trnKeys.section_shadow),
      symbol: <ShadowBold />,
    },
  ];
  const fillTypes = [
    { value: 'media', label: translate(trnKeys.section_fill_media_btn) },
    { value: 'gradient', label: translate(trnKeys.section_fill_gradient_btn) },
  ];

  const gradient = getColorOrGradient(media);
  const [currentTab, setTab] = React.useState('color');
  const [fillType, setFillType] = React.useState(backgroundType);
  const [isColorOverlay, setIsColorOverlay] = React.useState(!!colorOverlay);
  const [lastColorOverlay, setLastColorOverlay] =
    React.useState<ColorOverlayData>(DEFAULT_OVERLAY);

  function onGradientChangeCallback(color: ColorOrGradient) {
    const newColorLayers = getNewColorLayers(
      media,
      color,
      getColorOpacityValue(media) ?? 1,
    );
    updateDesignWithBI(
      {
        media: {
          ...media,
          ...COLOR_DEFAULTS,
          colorLayers: newColorLayers,
          showOverlayForMediaType: 'all',
        },
      },
      { fieldNames: 'color', fieldValues: newColorLayers?.[0].fill.type },
    );
  }

  function updateDesignWithMeasures(
    newCompDesign: Partial<TextMaskDesign>,
    biParams: BiParams,
  ) {
    const newViewBox =
      measureSvgRef.current?.getViewbox({
        ...compData,
        ...compDesign,
        ...newCompDesign,
      }) || viewBox;
    updateDesignWithBI({ ...newCompDesign, viewBox: newViewBox }, biParams);
    if (compLayout && updateLayout) {
      const widthFromLayout = compLayout.width;
      const [width, height] = newViewBox
        .split(' ')
        .slice(2)
        .map((str) => parseInt(str, 10));
      const aspectRatio = width / height;
      const newHeight = widthFromLayout / aspectRatio;
      updateLayout({ height: newHeight });
    }
  }

  function updateDesignWithBI(
    newCompDesign: Partial<TextMaskDesign>,
    { fieldNames = '', fieldValues = '' }: BiParams,
  ) {
    updateDesign(newCompDesign);
    sendBI({ fieldNames, fieldValues });
  }

  function onChangeMediaCallback(payload: MMGRFileInfo[], info: MMGRInfo) {
    if (!payload) {
      return;
    }
    const payloadItem = payload[0];
    const mediaType = payloadItem.mediaType;
    const DEFAULT_BLEND_MODE = 'overlay';

    let blendMode = overlayBlending?.blendMode;
    let mediaObj: BackgroundData['mediaRef'];
    let fittingType: FittingType = fittingTypes.SCALE_TO_FILL;

    if (mediaType === payloadMediaTypes.VIDEO) {
      if (blendMode && BLEND_MODES_IMG_ONLY[blendMode]) {
        blendMode = DEFAULT_BLEND_MODE;
      }
      mediaObj = getVideoBackgroundObject(
        payloadItem,
        info,
      ) as unknown as VideoData;
    } else if (mediaType === payloadMediaTypes.IMAGE) {
      mediaObj = getImageBackgroundObject(
        payloadItem,
        info,
      ) as unknown as ImageData;

      // set default fitting and alignment
      const { width, height } = mediaObj;
      const isSmaller = isSmallerFromContainer(
        width,
        height,
        DIMENSIONS.PANEL_WIDTH,
        DIMENSIONS.PANEL_HEIGHT,
      );
      fittingType = isSmaller ? fittingTypes.FIT_AND_TILE : fittingType;
    }
    // Preserve alt
    if (mediaObj && mediaObj?.alt === 'undefined') {
      mediaObj.alt = mediaObj?.title;
    }
    // Preserve opacity
    // mediaObj.opacity = this.props.bgData.mediaRef?.opacity ?? 1;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    const newMedia = {
      ...mediaNoColors,
      fittingType,
      alignType: alignTypes.CENTER,
      mediaRef: mediaObj,
      showOverlayForMediaType: 'all',
      ...(blendMode && { overlayBlending: { blendMode } }),
    };

    updateDesignWithBI(
      { media: newMedia },
      { fieldNames: mediaObj?.type, fieldValues: mediaObj?.id || '' },
    );
  }

  function openColorPicker(
    color: ColorOrGradient | undefined = '#FFFFFF',
    onChange: (newValue: ColorOrGradient) => void,
    rect: DOMRect,
    panelMode: PanelMode = 'solid',
  ) {
    const position = {
      top: rect.top + COLOR_PICKER_Y_OFFSET,
      left: rect.left + COLOR_PICKER_X_OFFSET,
    };

    const _onChange = (value: ColorOrGradient | null) => {
      if (value !== null) {
        onChange(value);
      }
    };
    const _onCancel = _onChange;

    openColorPickerFromProps(color, position, {
      onChange: _onChange,
      onCancel: _onCancel,
      panelMode,
      overridePosition: false,
      showColorModalConfirmButtons: false,
    });
  }

  function handleChangeGradient(e: MouseEvent) {
    const target = e.currentTarget as HTMLElement;
    openColorPicker(
      getColorOrGradient(media),
      onGradientChangeCallback,
      target.getBoundingClientRect(),
      'gradientOnly',
    );
  }

  function handleChangeMedia() {
    const { mediaManager, setFocusToEditor } = mediaServices;

    mediaManager.open(mediaCategory, {
      callback: (items: MMGRFileInfo[], info: MMGRInfo) => {
        onChangeMediaCallback(items, info);
        setFocusToEditor();
      },
      path:
        getMediaType(media) === 'Image'
          ? mediaManagerDefaultPaths.Image
          : mediaManagerDefaultPaths.WixVideo,
    });
  }

  function onBackgroundTypeChange(value: 'media' | 'gradient') {
    if (fillType === value) {
      return;
    }
    const currentMedia = JSON.parse(
      JSON.stringify(compDesign.media),
    ) as BackgroundData;
    const nextMedia = (previousMedia.current ||
      mediaDefaults[value]) as BackgroundData;

    let biParams: BiParams = { fieldNames: '', fieldValues: '' };
    if (value === 'gradient') {
      nextMedia.colorLayers![0].opacity = currentMedia.mediaRef?.opacity ?? 1;
      biParams = {
        fieldNames: 'color',
        fieldValues: nextMedia.colorLayers![0].fill.type,
      };
    } else {
      nextMedia.mediaRef!.opacity = currentMedia.colorLayers?.[0].opacity ?? 1;
      nextMedia.colorLayers![0].opacity = 0;
      biParams = {
        fieldNames: nextMedia.mediaRef!.type,
        fieldValues: nextMedia.mediaRef!.id,
      };
    }
    updateDesignWithBI({ media: nextMedia }, biParams);
    previousMedia.current = currentMedia;
    setFillType(value);
  }

  const handleMediaOpacityChange = (newValue: number) => {
    if (mediaRef) {
      updateDesignWithBI(
        {
          media: {
            ...media,
            mediaRef: { ...mediaRef, opacity: newValue / 100 },
          },
        },
        { fieldNames: 'media_opacity', fieldValues: newValue + '' },
      );
    }
  };

  const handleOverlayOpacityChange = (newValue: number) => {
    if (mediaRef) {
      updateDesignWithBI(
        {
          media: {
            ...media,
            colorOverlayOpacity: newValue / 100,
          },
        },
        { fieldNames: 'color_overlay_opacity', fieldValues: `${newValue}` },
      );
    }
  };

  const handleOverlayBlendChange = (newValue: BlendMode) => {
    if (overlayBlending) {
      updateDesignWithBI(
        {
          media: {
            ...media,
            overlayBlending: { blendMode: newValue, opacity: 1 },
          },
        },
        { fieldNames: 'color_overlay_blend', fieldValues: newValue },
      );
    }
  };

  const handleGradientOpacityChange = (newValue: number) => {
    if (colorLayers) {
      updateDesignWithBI(
        {
          media: {
            ...media,
            colorLayers: [{ ...colorLayers[0], opacity: newValue / 100 }],
          },
        },
        { fieldNames: 'color_opacity', fieldValues: newValue + '' },
      );
    }
  };
  const handleOverlayColorChange: React.MouseEventHandler = (e) => {
    openColorPicker(
      colorOverlay as string,
      handleColorPickerValueChange,
      e.currentTarget.getBoundingClientRect(),
    );
  };

  const handleColorPickerValueChange = (newValue: ColorOrGradient) => {
    updateDesignWithBI(
      {
        media: {
          ...media,
          colorOverlay: newValue as string,
        },
      },
      { fieldNames: 'color_overlay', fieldValues: `${newValue}` },
    );
  };

  const handleColorOverlayToggle = (isOverlay: boolean) => {
    setIsColorOverlay(isOverlay);
    if (isOverlay) {
      updateDesignWithBI(
        {
          media: {
            ...media,
            ...lastColorOverlay,
          },
        },
        {
          fieldNames: 'color_overlay_apply',
          fieldValues: 'on',
        },
      );
    } else {
      setLastColorOverlay({
        colorOverlay,
        colorOverlayOpacity,
        overlayBlending,
      });
      updateDesignWithBI(
        {
          media: {
            ...media,
            colorOverlay: null,
            colorOverlayOpacity: 1,
            overlayBlending: {},
          },
        },
        {
          fieldNames: 'color_overlay_apply',
          fieldValues: 'off',
        },
      );
    }
  };

  // const gradient = getColorOrGradient(media);

  const TextTab = () => (
    <TextSection
      fonts={fonts}
      fontThemes={fontThemes}
      allowThemeControl={allowThemeControl}
      family={family}
      openLanguagesPanel={openLanguagesPanel}
      openUploadFonts={openUploadFonts}
      autoScale={false}
      size={size}
      allowAutoScaleControl={allowAutoScaleControl}
      weight={weight}
      style={style}
      decoration={decoration}
      align={align}
      direction={direction}
      transform={transform}
      letterSpacing={letterSpacing}
      lineHeight={lineHeight}
      headingLevel={headingLevel}
      isDecorative={isDecorative}
      updateDesignWithBI={updateDesignWithMeasures}
      updateData={updateData}
      translate={translate}
      textLabelType={textLabelType}
    />
  );

  const ShadowTab = () => (
    <>
      <div className={styles.tabHeader}>
        <Text shouldTranslate={false}>{translate(trnKeys.section_shadow)}</Text>
      </div>
      <ShadowSection
        shadow={shadow}
        updateDesignWithBI={updateDesignWithBI}
        openColorPicker={openColorPicker}
        colorResolver={colorResolver}
        translate={translate}
        textLabelType={textLabelType}
      />
    </>
  );

  const FillColorTab = () => (
    <div className={styles.colorTab}>
      <div className={styles.tabHeader}>
        <Text weight="bold" shouldTranslate={false}>
          {translate(trnKeys.section_fill)}
        </Text>
      </div>
      <HorizontalTabs
        value={fillType}
        type="arrowed"
        onChange={onBackgroundTypeChange}
        options={fillTypes}
        shouldTranslate={false}
      />
      {fillType === 'media' && (
        <>
          <div
            className={styles.mediaImage}
            style={{
              height: `${DIMENSIONS.MEDIA_HEIGHT}px`,
              backgroundImage: `url(${getPreviewImage(media)})`,
            }}
          ></div>
          <Divider long />
          <Button
            shouldTranslate={false}
            className={`btn-md ${styles.changeMediaBtn}`}
            onClick={handleChangeMedia}
          >
            {translate(trnKeys.section_fill_change_media)}
          </Button>
          <Divider long />
          <Composites.SliderLabeled>
            <TextLabel
              shouldTranslate={false}
              value={translate(trnKeys.section_fill_media_opacity)}
            />
            <Slider
              value={Math.round((mediaRef?.opacity ?? 1) * 100)}
              placeholder=""
              min={0}
              max={100}
              step={1}
              onChange={handleMediaOpacityChange}
              unit="percent"
              shouldTranslate={false}
            />
          </Composites.SliderLabeled>
          <Divider long />
          <Composites.ToggleSwitch>
            <ToggleSwitch
              label={translate(trnKeys.section_fill_overlay_color_apply)}
              value={isColorOverlay}
              onChange={handleColorOverlayToggle}
              shouldTranslate={false}
            />
          </Composites.ToggleSwitch>

          {isColorOverlay && (
            <>
              <Divider long={false} />
              <Composites.ColorPickerInputLabeled>
                <TextLabel
                  value={translate(trnKeys.section_fill_overlay_opacity)}
                  shouldTranslate={false}
                />
                <Composites.ColorPickerInput>
                  <OpacityPicker
                    value={Math.round((colorOverlayOpacity ?? 1) * 100)}
                    onChange={handleOverlayOpacityChange}
                    opacityGradientColor={colorResolver(colorOverlay || '')}
                  />
                  <ColorPickerInput
                    value={colorResolver(colorOverlay || '')}
                    opacity={100}
                    onClick={handleOverlayColorChange}
                  />
                </Composites.ColorPickerInput>
              </Composites.ColorPickerInputLabeled>
              <Divider long={false} />
              <Composites.DropDownLabeled>
                <TextLabel
                  type="T02"
                  value={translate(trnKeys.section_fill_overlay_blend)}
                  shouldTranslate={false}
                />
                <DropDown
                  value={overlayBlending?.blendMode}
                  onChange={handleOverlayBlendChange}
                  shouldTranslate={false}
                >
                  {Object.entries(blendModes).map(([mode, label]) => (
                    <DropDownOption
                      value={mode}
                      label={translate(label)}
                      shouldTranslate={false}
                      key={`blend${mode}`}
                    />
                  ))}
                </DropDown>
              </Composites.DropDownLabeled>
            </>
          )}
        </>
      )}
      {fillType === 'gradient' && (
        <>
          <Composites.ColorPickerInputLabeled>
            <TextLabel
              value={translate(trnKeys.section_fill_overlay_opacity_gradient)}
              shouldTranslate={false}
            />
            <Composites.ColorPickerInput>
              <OpacityPicker
                value={Math.round((getColorOpacityValue(media) ?? 1) * 100)}
                onChange={handleGradientOpacityChange}
                opacityGradientColor={colorResolver(colorOverlay || '')}
              />
              <ColorPickerInput
                value={getResolvedColor(gradient || '#000', colorResolver)}
                opacity={100}
                onClick={handleChangeGradient}
              />
            </Composites.ColorPickerInput>
          </Composites.ColorPickerInputLabeled>
          <Divider long />
        </>
      )}
    </div>
  );

  const renderTab = {
    color: FillColorTab(),
    text: TextTab(),
    shadow: ShadowTab(),
  };

  return (
    <>
      <SvgMeasure ref={measureSvgRef} />
      <div className={styles.panelContainer}>
        <VerticalTabsCustom
          value={currentTab}
          onChange={setTab}
          options={panelTabOptions}
          closed={false}
          shouldTranslate={false}
        />
        <CustomScroll heightRelativeToParent="100%">
          {renderTab[currentTab as keyof typeof renderTab]}
        </CustomScroll>
      </div>
    </>
  );
};
